import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { translations } from '../../translations/translations';
import dayjs from 'dayjs';
import CompactVoiceRecorder from './CompactVoiceRecorder';

const AddInteractionDialog = ({ 
  open, 
  onClose, 
  onAdd, 
  contactId, 
  language = 'en',
  initialNotes = '',
  title,
  helperText,
}) => {
  // Добавляем дефолтные значения для переводов
  const defaultTranslations = {
    addInteraction: 'Add touch',
    interactionDate: 'Date & Time',
    notes: 'Notes',
    cancel: 'Cancel',
    save: 'Save',
    required: 'This field is required',
    textInput: 'Text Input',
    voiceRecording: 'Voice Recording'
  };
  
  const t = { ...defaultTranslations, ...translations[language] };
  
  const [interactionData, setInteractionData] = useState({
    date: dayjs(),
    notes: initialNotes,
  });

  useEffect(() => {
    if (open) {
      setInteractionData({
        date: dayjs(),
        notes: initialNotes || '',
      });
    }
  }, [initialNotes, open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInteractionData({
      ...interactionData,
      [name]: value,
    });
  };

  const handleDateChange = (newDate) => {
    setInteractionData({
      ...interactionData,
      date: newDate,
    });
  };

  const handleTranscriptionComplete = (transcribedText) => {
    // Add transcribed text to notes field
    setInteractionData(prev => ({
      ...prev,
      notes: prev.notes ? `${prev.notes}\n\n${transcribedText}` : transcribedText
    }));
  };

  const handleVoiceRecordingError = (error) => {
    console.error('Voice recording error:', error);
    // You could show a snackbar or alert here
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      date: interactionData.date.toISOString(),
      notes: interactionData.notes,
    };

    await onAdd(contactId, data);

    // Сброс значений
    setInteractionData({
      date: dayjs(),
      notes: initialNotes || '',
    });

    onClose();
  };

  const handleClose = () => {
    // Reset form data when closing
    setInteractionData({
      date: dayjs(),
      notes: initialNotes || '',
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        {title || t.addInteraction}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label={t.interactionDate}
              value={interactionData.date}
              onChange={handleDateChange}
              renderInput={(params) => <TextField {...params} fullWidth sx={{ mb: 2 }} />}
              sx={{ width: '100%', mb: 3 }}
            />
          </LocalizationProvider>

          <TextField
            fullWidth
            multiline
            rows={6}
            name="notes"
            label={t.notes}
            value={interactionData.notes}
            onChange={handleChange}
            placeholder="What happened? What should you remember next time?"
            helperText={helperText || 'A short memory is enough. Calls, messages, meetings, and thoughtful moments all count.'}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '0.9375rem',
                lineHeight: 1.6
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ alignSelf: 'center', mr: -0.5 }}>
                  <CompactVoiceRecorder 
                    onTranscriptionComplete={handleTranscriptionComplete}
                    onError={handleVoiceRecordingError}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t.cancel}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!interactionData.notes.trim()} // Disable if no notes
        >
          {t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddInteractionDialog;
